class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    // Vorherige Requests abbrechen, damit veraltete Responses nicht die aktuellen überschreiben
    this.abortController.abort();
    this.abortController = new AbortController();

    const shopifyFetch = fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`,
      { signal: this.abortController.signal }
    ).then((response) => {
      if (!response.ok) throw new Error(response.status);
      return response.text();
    });

    const ftkFetch =
      searchTerm.length >= 2
        ? fetch(
            `https://sto-projektberater-v3.farbenbote.workers.dev/api/ftk/search?q=${encodeURIComponent(searchTerm)}&limit=8`,
            { signal: this.abortController.signal }
          ).then((r) => (r.ok ? r.json() : { results: [], total: 0 }))
        : Promise.resolve(null);

    Promise.allSettled([shopifyFetch, ftkFetch])
      .then(([shopifyResult, ftkResult]) => {
        // Staleness-Check: Suchbegriff hat sich zwischenzeitlich geändert
        if (this.searchTerm !== searchTerm) return;

        if (shopifyResult.status === 'rejected') {
          if (shopifyResult.reason?.code === 20) return;
          this.close();
          throw shopifyResult.reason;
        }

        const resultsMarkup = new DOMParser()
          .parseFromString(shopifyResult.value, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;

        let finalMarkup = resultsMarkup;

        // Inject FTK color results into the left column
        if (ftkResult.status === 'fulfilled' && ftkResult.value && ftkResult.value.results && ftkResult.value.results.length > 0) {
          const ftkHtml = this.buildFtkColorResults(ftkResult.value, searchTerm);
          const tempDoc = new DOMParser().parseFromString(`<div>${finalMarkup}</div>`, 'text/html');
          const wrapper = tempDoc.querySelector('.predictive-search__results-groups-wrapper');
          if (wrapper) {
            const firstGroup = wrapper.querySelector('.predictive-search__result-group');
            if (firstGroup) {
              // FTK-Gruppe VOR die erste Gruppe einfügen — niemals ersetzen: liefert Shopify
              // nur Produkte, wäre die erste Gruppe die Produkt-Gruppe und würde verschwinden
              firstGroup.insertAdjacentHTML('beforebegin', ftkHtml);
              finalMarkup = tempDoc.body.firstChild.innerHTML;
            }
          } else {
            // Shopify hat keine Ergebnisse (kein Wrapper vorhanden) — FTK-only Ergebnisse anzeigen
            const searchForBtn = tempDoc.querySelector('.predictive-search__search-for-button');
            const ftkWrapper = `<div id="predictive-search-results-groups-wrapper" class="predictive-search__results-groups-wrapper predictive-search__results-groups-wrapper--no-products">${ftkHtml}</div>`;
            if (searchForBtn) {
              searchForBtn.insertAdjacentHTML('beforebegin', ftkWrapper);
            } else {
              const container = tempDoc.querySelector('#predictive-search-results');
              if (container) container.insertAdjacentHTML('afterbegin', ftkWrapper);
            }
            finalMarkup = tempDoc.body.firstChild.innerHTML;
          }
        }

        // Nur cachen wenn FTK erfolgreich war oder nicht angefragt wurde,
        // damit fehlgeschlagene FTK-Responses nicht als leeres Ergebnis gecacht werden
        const ftkWasRequested = searchTerm.length >= 2;
        const ftkSucceeded = ftkResult.status === 'fulfilled';
        if (!ftkWasRequested || ftkSucceeded) {
          this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
            predictiveSearchInstance.cachedResults[queryKey] = finalMarkup;
          });
        }
        this.renderSearchResults(finalMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) return;
        this.close();
      });
  }

  buildFtkColorResults(data, searchTerm) {
    const results = data.results.slice(0, 8);
    const total = data.total || results.length;
    // Korrektur der naiven Title-Case-Kapitalisierung für Marken-/Systemnamen
    // (nur fürs Anzeige-Label — der Slug in Links bleibt unverändert)
    const cardLabelFixes = {
      ral: 'RAL',
      ncs: 'NCS',
      stocolor: 'StoColor',
      sto: 'Sto',
      caparol: 'Caparol',
      sikkens: 'Sikkens',
      akzo: 'Akzo',
      nobel: 'Nobel',
      ac: 'AC',
    };
    const items = results
      .map((r) => {
        const name = r.n || r.i;
        const hex = r.h || '#888888';
        const slug = r.c || '';
        const cardLabel = slug
          .split('-')
          .map((w) => cardLabelFixes[w.toLowerCase()] || w.charAt(0).toUpperCase() + w.slice(1))
          .join(' ');
        return `<li role="option" aria-selected="false" class="predictive-search__list-item">
          <a href="/pages/farbtonfinder?card=${encodeURIComponent(slug)}&ton=${encodeURIComponent(r.i)}" class="predictive-search__item predictive-search__item--color link link--text" tabindex="-1">
            <span class="predictive-search__color-swatch" style="background-color:${hex}"></span>
            <div class="predictive-search__item-content predictive-search__item-content--centered">
              <p class="predictive-search__item-heading h5">${this.escapeHtml(name)}</p>
              <span class="predictive-search__item-vendor caption-with-letter-spacing">${this.escapeHtml(cardLabel)}</span>
            </div>
          </a>
        </li>`;
      })
      .join('');

    return `<div class="predictive-search__result-group">
      <div>
        <h2 class="predictive-search__heading text-body caption-with-letter-spacing">Farbt\u00F6ne</h2>
        <ul class="predictive-search__results-list list-unstyled" role="group">${items}</ul>
      </div>
      <div class="predictive-search__ftk-see-all-wrapper">
        <a href="/pages/farbtonfinder?q=${encodeURIComponent(searchTerm || '')}" class="predictive-search__item predictive-search__item--term link link--text h5">
          Alle ${total} Farbt\u00F6ne durchsuchen \u2192
        </a>
      </div>
    </div>`;
  }

  escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const countEl = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (countEl) this.setLiveRegionText(countEl.textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
